'use client';

import { bunja } from 'bunja';
import { createScopeFromContext, useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { AnimatePresence } from 'motion/react';
import { PropsWithChildren, ReactNode, createContext, useId, useMemo } from 'react';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import deferred, { Deferred } from '@flipster/utils/deferred';
import { generateID } from '@flipster/utils/identity';

export function useOverlay() {
  const { open, close } = useBunja(overlayBunja);
  const overlay = useMemo(() => ({ open, close }), [open, close]);
  return overlay;
}

export function useLocalOverlay() {
  const overlayId = useId();
  const { open, close, presenceItemsAtom } = useBunja(overlayBunja, [OverlayIdScope.bind(overlayId)]);
  const presenceItems = useAtomValue(presenceItemsAtom);
  const placeholder = useMemo(
    () => (
      <OverlayIdContext value={overlayId}>
        <AnimatePresence>{presenceItems}</AnimatePresence>
      </OverlayIdContext>
    ),
    [overlayId, presenceItems],
  );
  const overlay = useMemo(() => ({ open, close, placeholder }), [open, close, placeholder]);
  return overlay;
}

export function useOverlayItem() {
  const { isOpenAtom, close } = useBunja(overlayItemBunja);
  const isOpen = useAtomValue(isOpenAtom);
  const onClose = close;
  return { isOpen, onClose };
}

export const OverlayProvider = ({ children }: PropsWithChildren) => {
  const { presenceItemsAtom } = useBunja(overlayBunja);
  const presenceItems = useAtomValue(presenceItemsAtom);
  return (
    <>
      {children}
      <AnimatePresence>{presenceItems}</AnimatePresence>
    </>
  );
};

const OverlayIdContext = createContext<string>('');
const OverlayIdScope = createScopeFromContext(OverlayIdContext);

export const overlayBunja = bunja(() => {
  bunja.use(OverlayIdScope);
  const store = bunja.use(JotaiStoreScope);
  const closedTable: Record<string, Deferred<void>> = {};

  const slotsAtom = atom<Record<string, OverlayItem>>({});
  const presenceItemsAtom = atom((get) => {
    const slots = get(slotsAtom);
    return Object.values(slots)
      .filter(Boolean)
      .map((item) => (
        <OverlayItemContext value={item} key={item.id}>
          {item.reactNode}
        </OverlayItemContext>
      ));
  });

  function open(content: ReactNode | ((close: () => void) => ReactNode), slot = 'modal') {
    {
      // close previous
      const prevItem = store.get(slotsAtom)[slot];
      if (prevItem) closeById(prevItem.id);
    }
    {
      // open new
      const id = generateID('overlay-');
      // oxlint-disable-next-line @typescript-eslint/no-shadow
      const close = () => closeById(id);
      const closed = deferred<void>();
      closedTable[id] = closed;
      const reactNode = typeof content === 'function' ? content(close) : content;
      store.set(slotsAtom, (prev) => ({ ...prev, [slot]: { id, slot, reactNode } }));
      return { close, closed };
    }
  }

  function close(slot = 'modal') {
    const item = store.get(slotsAtom)[slot];
    if (item) closeById(item.id);
  }

  function closeById(id: string) {
    if (id in closedTable) {
      closedTable[id].resolve();
      delete closedTable[id];
    }
    const slots = { ...store.get(slotsAtom) };
    for (const slot of Object.keys(slots)) {
      if (slots[slot].id === id) delete slots[slot];
    }
    store.set(slotsAtom, slots);
  }

  function getClosed(id: string): Promise<void> {
    if (!closedTable[id]) return Promise.resolve();
    return closedTable[id];
  }

  return { presenceItemsAtom, open, close, closeById, getClosed };
});

interface OverlayItem {
  id: string;
  slot: string;
  reactNode: ReactNode;
}

const OverlayItemContext = createContext<OverlayItem>(null as unknown as OverlayItem);
const OverlayItemScope = createScopeFromContext(OverlayItemContext);

export const overlayItemBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const overlayItem = bunja.use(OverlayItemScope);
  const overlayBunjaInstance = bunja.use(overlayBunja);
  const isOpenAtom = atom(true);

  function close() {
    overlayBunjaInstance.closeById(overlayItem.id);
  }

  bunja.effect(() => {
    overlayBunjaInstance.getClosed(overlayItem.id).then(() => store.set(isOpenAtom, false));
    return () => {};
  });

  return {
    isOpenAtom,
    close,
  };
});
